import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { FilterPlanel } from "./filterPanel";
import { ActivityReport } from "./activityReport";
import { ShiftReport } from "./shiftReport";
import { TableLoadingAnimation } from "../../../components/loading/tableLoading";
import { AdjustmentsHorizontalIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { PageHeading } from "../../../components/pageHeading";
import { DownloadPdfModal } from "../../../components/modal/downloadPDFModal";

function Reports() {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();
  const hasPDFAccess =
    authContext.organization.planFeatures?.PDFReports !== false;

  // When the component mounts, no search has been run yet.
  const [hasRunSearch, setHasRunSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filtersState, setFiltersState] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [reportType, setReportType] = useState("activity");
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfError, setPdfError] = useState(false);

  const constructApiURL = (filters, endPoint) => {
    const {
      selectedCustomers,
      selectedSites,
      selectedActivityTypes,
      selectedUsers,
      dateRange,
      reportType,
    } = filters;

    if (!reportType) {
      console.error("Report type not specified");
      return;
    }

    if (!orgId) {
      console.error("Organization ID not found");
      return;
    }

    if (!selectedCustomers && !selectedSites && !selectedUsers) {
      console.error("No filters selected");
      return;
    }

    let baseUrl;
    if (endPoint && endPoint.toLowerCase() === "pdf") {
      // Use the PDF endpoint base.
      baseUrl = `${process.env.REACT_APP_API_URL}/v1/pdfs/reports/${reportType}?org_id=${orgId}`;
    } else {
      baseUrl = `${process.env.REACT_APP_API_URL}/v1/reporting/reports/${reportType}?org_id=${orgId}`;
    }

    if (selectedCustomers?.length > 0) {
      const customerIds = selectedCustomers.join(",");
      baseUrl += `&customer_id=${customerIds}`;
    }

    if (selectedSites?.length > 0) {
      const siteIds = selectedSites.join(",");
      baseUrl += `&site_ids=${siteIds}`;
    }

    if (selectedActivityTypes?.length > 0) {
      const activityTypeIds = selectedActivityTypes.join(",");
      baseUrl += `&activity_type_ids=${activityTypeIds}`;
    }

    if (selectedUsers?.length > 0) {
      const userIds = selectedUsers.join(",");
      baseUrl += `&user_ids=${userIds}`;
    }

    if (dateRange?.startDate) {
      baseUrl += `&start_date=${dateRange.startDate}`;
    }
    if (dateRange?.endDate) {
      baseUrl += `&end_date=${dateRange.endDate}`;
    }

    return baseUrl;
  };

  // Fetches the data from the API (for report data)
  const fetchReportsData = async (url) => {
    if (!orgId) return;
    setIsLoading(true);


    const fetchPromise = authFetch(url)
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          setReportData(data);
        } else {
          console.error("HTTP Error: ", response.statusText);
        }
      })
      .catch((error) => {
        console.error("Fetching error: ", error.message);
      });

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, timerPromise]).then(() => {
      setIsLoading(false);
    });
  };

  // When filters are run, store them and clear out any existing PDF URL.
  const handleRunFilters = (filters) => {
    setHasRunSearch(true);
    setFiltersState(filters);
    setReportType(filters.reportType); // drive which report component renders
    setPdfUrl(""); // Clear out any previously generated PDF URL
    const currentApiURL = constructApiURL(filters);
    fetchReportsData(currentApiURL);
  };

  // When the user clicks "Download PDF"
  const handleDownloadPdf = (filters) => {
    if (pdfUrl !== "") {
      setPdfUrl("");
    }
    const currentApiURL = constructApiURL(filters, "pdf");
    fetchReportPDF(currentApiURL);
  };

  // Programmatically start the download. The PDF URL is a presigned S3 link
  // served with Content-Disposition: attachment, so this drops the file
  // straight into the browser's downloads without navigating away.
  const triggerDownload = (fileUrl) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate the PDF, then download it automatically (no second click).
  const fetchReportPDF = async (url) => {
    if (!orgId) return;
    setPdfError(false);
    setIsPDFLoading(true);
    setPdfModalOpen(true);

    try {
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.pdfUrl) {
          triggerDownload(data.pdfUrl);
          setPdfModalOpen(false); // the download itself is the confirmation
        } else {
          setPdfError(true);
        }
      } else {
        console.error("HTTP Error: ", response.statusText);
        setPdfError(true);
      }
    } catch (error) {
      console.error("Fetching PDF error: ", error.message);
      setPdfError(true);
    } finally {
      setIsPDFLoading(false);
    }
  };

  return (
    <div className="flex flex-row flex-1 min-h-0">
      <div className="flex flex-1 flex-col py-6 px-6 min-h-0 overflow-y-auto">
        <PageHeading title="Reports" />
        <div className="flex flex-1 border border-gray-100 dark:border-gray-700 rounded-lg">
          {!hasRunSearch ? (
            <div className="flex flex-col m-6 items-center justify-center w-full">
              <AdjustmentsHorizontalIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                Select the filters & hit run to create your reports
              </p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center m-6 w-full">
              <TableLoadingAnimation isLoading={isLoading} />
            </div>
          ) : Object.keys(reportData).length > 0 &&
            (reportData.summary?.sites > 0 ||
              reportData.summary?.shifts > 0 ||
              reportData.sites?.length > 0 ||
              reportData.shifts?.length > 0) ? (
            <div className="flex flex-col w-full px-6 py-6">
              <div className="fixed bottom-5 left-1/2 transform -translate-x-1/2 shadow-lg bg-gray-800 text-white dark:bg-gray-100 dark:text-gray-900 rounded-lg text-sm">
                {hasPDFAccess ? (
                  <button
                    disabled={isPDFLoading}
                    onClick={() => handleDownloadPdf(filtersState)}
                    className="px-4 py-2 text-white hover:text-gray-100 dark:text-gray-900 dark:hover:text-gray-700 transition-all duration-200 ease-in-out disabled:opacity-70 disabled:cursor-wait"
                  >
                    {isPDFLoading ? "Generating…" : "Download PDF"}
                  </button>
                ) : (
                  <div className="relative group">
                    <button
                      disabled
                      className="px-4 py-2 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50"
                    >
                      Download PDF
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 z-50 shadow-lg">
                      <p>PDF reports are only available on paid plans</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {reportData.logo_url ? (
                    <img
                      src={reportData.logo_url}
                      alt={reportData.customer_name}
                      className="h-12 mr-2"
                    />
                  ) : (
                    <span className="text-md font-bold text-gray-800 dark:text-gray-100">
                      {reportData.customer_name ||
                        reportData.report_type ||
                        "Report"}
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 text-right">
                  {new Date(reportData.report_date).toLocaleDateString(
                    undefined,
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    },
                  )}
                </span>
              </div>
              <div>
                {reportType === "shift" ? (
                  <ShiftReport reportData={reportData} />
                ) : reportType === "incident" ? (
                  <div>Incident Report Component</div>
                ) : (
                  <ActivityReport reportData={reportData} />
                )}
              </div>
            </div>
          ) : (
            // No data was returned
            <div className="flex flex-col items-center justify-center w-full py-16">
              <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                No reports for the selected filters
              </p>
            </div>
          )}
        </div>
      </div>

      <div className=" dark:border-gray-700 flex flex-col relative z-10 h-full sticky top-0 self-stretch shrink-0">
        <FilterPlanel onUpdate={handleRunFilters} />
      </div>
      <DownloadPdfModal
        isOpen={pdfModalOpen}
        isLoading={isPDFLoading}
        error={pdfError}
        onClose={() => setPdfModalOpen(false)}
      />
    </div>
  );
}

export { Reports };
